/**
 * usePostReview - React Query hooks for post-level review operations
 *
 * Provides hooks for:
 * - Fetching posts for review (query)
 * - Fetching post detail with hierarchical mentions (query)
 * - Individual mention actions: approve, reject (mutations)
 * - Bulk mention actions: bulk approve/reject (mutations)
 * - Company/location scope setting (mutations)
 *
 * SESSION_051: List view queries
 * SESSION_052: Added mutation hooks for actions
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  postReviewApi,
  type CreateManualMentionRequest,
  type CreateManualMentionResponse
} from '@/infrastructure/api/PostReviewApi';
import {
  mentionReviewApi,
  type TriggerNotificationsResponse
} from '@/infrastructure/api/MentionReviewApi';
import { bulkActionsApi } from '@/infrastructure/api/BulkActionsApi';
import { mentionReviewKeys } from '@/application/queries/useMentionReview';
import { notificationService } from '@/infrastructure/notifications';
import { parseRedditUrl, toFullRedditPostId } from '@/domain/utils/redditUrlParser';
import {
  getConfidenceLevel,
  type PostSummaryDto,
  type PostMentionGroupDto,
  type MentionDto,
  type GetPostsForReviewParams
} from '@/domain/entities/PostReview';
import type { PagedResult } from '@/domain/entities/PagedResult';
import type {
  ApproveMentionRequest,
  RejectMentionRequest,
  SetMentionScopeRequest
} from '@/domain/entities/MentionReview';
import type {
  BulkReviewMentionsRequest,
  BulkReviewMentionsResponse
} from '@/infrastructure/api/BulkActionsApi';

/**
 * Query keys for post review
 *
 * Hierarchical key structure for efficient cache management:
 * - all: Base key for all post review queries
 * - lists: All list queries
 * - list(params): Specific list query with parameters
 * - detail(postId): Specific post detail query
 */
export const postReviewKeys = {
  all: ['post-review'] as const,
  lists: () => [...postReviewKeys.all, 'list'] as const,
  list: (params?: GetPostsForReviewParams) => [...postReviewKeys.lists(), { params }] as const,
  details: () => [...postReviewKeys.all, 'detail'] as const,
  detail: (postId: string) => [...postReviewKeys.details(), postId] as const
};

/**
 * usePostsForReview - Query hook for fetching posts for review
 *
 * Features:
 * - Automatic refetch on window focus
 * - Cache management (30 second stale time)
 * - Error handling with toast notifications
 * - Pagination support
 * - Filtering and sorting
 *
 * @param params - Query parameters (filters, pagination, sorting)
 * @param enabled - Whether query should run (default: true)
 * @returns React Query result with paged posts data
 *
 * @example
 * // Basic usage - fetch first page
 * const { data, isLoading, error } = usePostsForReview({
 *   page: 1,
 *   pageSize: 20
 * });
 *
 * @example
 * // With filters and sorting
 * const { data, isLoading } = usePostsForReview({
 *   page: 1,
 *   pageSize: 20,
 *   subreddit: 'Seattle',
 *   reviewStatus: 'AllPending',
 *   minMentionCount: 2,
 *   sortBy: 'PostDate',
 *   sortDirection: 'Desc'
 * });
 *
 * @example
 * // Disabled query (conditional fetching)
 * const { data, isLoading } = usePostsForReview(
 *   { page: 1 },
 *   !!userHasPermission // Only fetch if user has permission
 * );
 */
export function usePostsForReview(
  params?: GetPostsForReviewParams,
  enabled: boolean = true
) {
  return useQuery<PagedResult<PostSummaryDto>, Error>({
    queryKey: postReviewKeys.list(params),
    queryFn: () => postReviewApi.getPostsForReview(params),
    enabled,
    staleTime: 30000, // 30 seconds - data is fairly fresh
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2 // Retry failed requests twice
  });
}

/**
 * usePostMentions - Query hook for fetching hierarchical post detail
 *
 * Features:
 * - Automatic refetch on window focus
 * - Cache management (60 second stale time - detail data changes less frequently)
 * - Error handling with toast notifications
 * - Hierarchical structure: Post → Top-level mentions + Comments → Comment mentions
 *
 * @param postId - Reddit post ID (e.g., "t3_abc123")
 * @param enabled - Whether query should run (default: true if postId provided)
 * @returns React Query result with post detail and hierarchical mentions
 *
 * @example
 * // Fetch post details when modal opens
 * const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
 *
 * const { data, isLoading } = usePostMentions(
 *   selectedPostId || '',
 *   !!selectedPostId // Only fetch if postId is set
 * );
 *
 * @example
 * // Access hierarchical structure
 * const { data } = usePostMentions('t3_abc123');
 *
 * if (data) {
 *   console.log('Post title:', data.postTitle);
 *   console.log('Top-level mentions:', data.topLevelMentions);
 *   console.log('Comments with mentions:', data.comments);
 *
 *   // Iterate through comments
 *   data.comments.forEach(comment => {
 *     console.log('Comment author:', comment.authorUsername);
 *     console.log('Comment mentions:', comment.mentions);
 *   });
 * }
 */
export function usePostMentions(
  postId: string,
  enabled?: boolean
) {
  // Default enabled: true if postId is provided
  const shouldEnable = enabled !== undefined ? enabled : !!postId;

  return useQuery<PostMentionGroupDto, Error>({
    queryKey: postReviewKeys.detail(postId),
    queryFn: () => postReviewApi.getPostMentions(postId),
    enabled: shouldEnable,
    staleTime: 60000, // 60 seconds - detail data changes less frequently
    refetchOnWindowFocus: true,
    refetchOnMount: false, // Don't refetch on mount (preserve cache)
    retry: 2
  });
}

/**
 * Helper: Invalidate post list queries
 *
 * Useful for manual cache invalidation after mutations in SESSION_052
 *
 * @example
 * import { useQueryClient } from '@tanstack/react-query';
 * import { postReviewKeys } from './usePostReview';
 *
 * const queryClient = useQueryClient();
 *
 * // After bulk approve action
 * await queryClient.invalidateQueries({
 *   queryKey: postReviewKeys.lists()
 * });
 */

/**
 * Helper: Prefetch post detail
 *
 * Useful for optimistic loading when hovering over a card
 *
 * @example
 * import { useQueryClient } from '@tanstack/react-query';
 * import { postReviewApi } from '@/infrastructure/api/PostReviewApi';
 * import { postReviewKeys } from './usePostReview';
 *
 * const queryClient = useQueryClient();
 *
 * const handleCardHover = (postId: string) => {
 *   queryClient.prefetchQuery({
 *     queryKey: postReviewKeys.detail(postId),
 *     queryFn: () => postReviewApi.getPostMentions(postId)
 *   });
 * };
 */

/**
 * SESSION_052: Mutation Hooks for Post Review Actions
 */

/**
 * Sprint 067: Optimistic cache mutation for approve/reject on post detail.
 *
 * Removes the mention wherever it lives (top-level or inside any comment) and
 * decrements the aggregate counts so downstream UI (badges, sort keys, stats
 * row) stays honest. Returns the same reference when the id isn't found, so
 * React skips a rerender in pathological double-dispatch cases.
 */
function removeMentionFromPostDetail(
  detail: PostMentionGroupDto,
  mentionId: string
): PostMentionGroupDto {
  let removed: MentionDto | null = null;

  const stripFromMentions = (mentions: MentionDto[]): MentionDto[] =>
    mentions.filter((m) => {
      if (m.id !== mentionId) return true;
      removed = m;
      return false;
    });

  const topLevelMentions = stripFromMentions(detail.topLevelMentions);
  const comments = detail.comments.map((c) => ({
    ...c,
    mentions: stripFromMentions(c.mentions)
  }));

  if (removed === null) return detail;

  const removedMention = removed as MentionDto;
  const band = getConfidenceLevel(removedMention.matchConfidenceScore);

  return {
    ...detail,
    topLevelMentions,
    comments,
    totalMentionCount: Math.max(0, detail.totalMentionCount - 1),
    pendingCount: removedMention.reviewStatus === 'Pending'
      ? Math.max(0, detail.pendingCount - 1)
      : detail.pendingCount,
    approvedCount: removedMention.reviewStatus === 'Approved'
      ? Math.max(0, detail.approvedCount - 1)
      : detail.approvedCount,
    rejectedCount: removedMention.reviewStatus === 'Rejected'
      ? Math.max(0, detail.rejectedCount - 1)
      : detail.rejectedCount,
    highConfidenceCount: band === 'high'
      ? Math.max(0, detail.highConfidenceCount - 1)
      : detail.highConfidenceCount,
    mediumConfidenceCount: band === 'medium'
      ? Math.max(0, detail.mediumConfidenceCount - 1)
      : detail.mediumConfidenceCount,
    lowConfidenceCount: band === 'low'
      ? Math.max(0, detail.lowConfidenceCount - 1)
      : detail.lowConfidenceCount
  };
}

/**
 * useApproveMentionInPost - Mutation hook for approving a single mention on
 * the post detail view.
 *
 * Sprint 067: ported optimistic pattern from `useMentionReview.useApproveMention`.
 * The `postReviewKeys.detail(postId)` cache is mutated authoritatively in
 * `onMutate` (no invalidation on success). `postReviewKeys.lists()` is still
 * marked stale in `onSettled` so the post-review list page refreshes the next
 * time it's visited, but with `refetchType: 'inactive'` so it never competes
 * with the currently-mounted detail view.
 *
 * @param postId - Reddit post ID whose detail cache will be mutated
 * @returns React Query mutation result
 */
export function useApproveMentionInPost(postId: string) {
  const queryClient = useQueryClient();
  const detailKey = postReviewKeys.detail(postId);

  return useMutation<
    void,
    Error,
    { id: string; request?: ApproveMentionRequest },
    { previousDetail: PostMentionGroupDto | undefined }
  >({
    mutationFn: ({ id, request }) => mentionReviewApi.approveMention(id, request),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<PostMentionGroupDto>(detailKey);

      queryClient.setQueryData<PostMentionGroupDto>(detailKey, (old) => {
        if (!old) return old;
        return removeMentionFromPostDetail(old, id);
      });

      return { previousDetail };
    },
    onSuccess: () => {
      notificationService.showSuccess('Mention approved successfully');
    },
    onError: (error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(detailKey, context.previousDetail);
      }
      console.error('Failed to approve mention:', error);
      notificationService.showError('Failed to approve mention');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: postReviewKeys.lists(),
        refetchType: 'inactive'
      });
    }
  });
}

/**
 * useRejectMentionInPost - Mutation hook for rejecting a single mention on
 * the post detail view.
 *
 * Sprint 067: ported optimistic pattern from `useMentionReview.useRejectMention`.
 * Same cache strategy as `useApproveMentionInPost` — the detail cache is the
 * source of truth, the list cache is marked stale in the background.
 *
 * @param postId - Reddit post ID whose detail cache will be mutated
 * @returns React Query mutation result
 */
export function useRejectMentionInPost(postId: string) {
  const queryClient = useQueryClient();
  const detailKey = postReviewKeys.detail(postId);

  return useMutation<
    void,
    Error,
    { id: string; request: RejectMentionRequest },
    { previousDetail: PostMentionGroupDto | undefined }
  >({
    mutationFn: ({ id, request }) => mentionReviewApi.rejectMention(id, request),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<PostMentionGroupDto>(detailKey);

      queryClient.setQueryData<PostMentionGroupDto>(detailKey, (old) => {
        if (!old) return old;
        return removeMentionFromPostDetail(old, id);
      });

      return { previousDetail };
    },
    onSuccess: () => {
      notificationService.showSuccess('Mention rejected successfully');
    },
    onError: (error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(detailKey, context.previousDetail);
      }
      console.error('Failed to reject mention:', error);
      notificationService.showError('Failed to reject mention');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: postReviewKeys.lists(),
        refetchType: 'inactive'
      });
    }
  });
}

/**
 * useBulkReviewMentions - Mutation hook for bulk approve/reject operations
 *
 * Features:
 * - Bulk approve or reject multiple mentions
 * - Partial success handling
 * - Automatic cache invalidation (list + detail)
 * - Returns success/failure counts and error details
 *
 * @param postId - Reddit post ID for cache invalidation
 * @returns React Query mutation result with BulkReviewMentionsResponse
 *
 * @example
 * const { mutate: bulkReview, isPending } = useBulkReviewMentions('t3_abc123');
 *
 * const handleBulkApprove = (mentionIds: string[]) => {
 *   bulkReview(
 *     {
 *       mentionIds,
 *       action: 'Approve',
 *       adminNotes: 'Bulk approved from post detail view'
 *     },
 *     {
 *       onSuccess: (result) => {
 *         if (result.failureCount > 0) {
 *           showNotification({
 *             message: `${result.successCount}/${result.totalRequested} mentions approved. ${result.failureCount} failed.`,
 *             color: 'yellow'
 *           });
 *         } else {
 *           showNotification({
 *             message: `${result.successCount} mentions approved`,
 *             color: 'green'
 *           });
 *         }
 *       }
 *     }
 *   );
 * };
 *
 * @example
 * // Bulk reject with rejection reason
 * const handleBulkReject = (mentionIds: string[], reason: string) => {
 *   bulkReview(
 *     {
 *       mentionIds,
 *       action: 'Reject',
 *       rejectionReason: reason,
 *       adminNotes: 'Bulk rejected'
 *     },
 *     {
 *       onSuccess: (result) => {
 *         console.log('Success count:', result.successCount);
 *         console.log('Errors:', result.errors);
 *       }
 *     }
 *   );
 * };
 */
export function useBulkReviewMentions(postId: string) {
  const queryClient = useQueryClient();

  return useMutation<BulkReviewMentionsResponse, Error, BulkReviewMentionsRequest>({
    mutationFn: (request) => bulkActionsApi.bulkReviewMentions(request),
    onSuccess: () => {
      // Invalidate both list and detail caches after bulk operation
      queryClient.invalidateQueries({ queryKey: postReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: postReviewKeys.detail(postId) });
    }
  });
}

/**
 * useSetMentionScope - Mutation hook for setting mention scope (company vs specific location)
 *
 * Features:
 * - Set location specificity: "Specific", "AllLocations", or "Ambiguous"
 * - Automatic cache invalidation (list + detail)
 * - Used for disambiguating franchise mentions
 *
 * @param postId - Reddit post ID for cache invalidation
 * @returns React Query mutation result
 *
 * @example
 * const { mutate: setScope, isPending } = useSetMentionScope('t3_abc123');
 *
 * // Set to specific location
 * const handleSetSpecific = (mentionId: string, locationId: number) => {
 *   setScope(
 *     {
 *       id: mentionId,
 *       request: {
 *         locationSpecificity: 'Specific',
 *         specificBusinessLocationId: locationId,
 *         adminNotes: 'Customer mentioned Pike Street location'
 *       }
 *     },
 *     {
 *       onSuccess: () => {
 *         showNotification({ message: 'Scope set to specific location', color: 'blue' });
 *       }
 *     }
 *   );
 * };
 *
 * @example
 * // Set to all locations (company mention)
 * const handleSetAllLocations = (mentionId: string) => {
 *   setScope(
 *     {
 *       id: mentionId,
 *       request: {
 *         locationSpecificity: 'AllLocations',
 *         adminNotes: 'Generic mention applies to all locations'
 *       }
 *     }
 *   );
 * };
 */
export function useSetMentionScope(postId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; request: SetMentionScopeRequest }>({
    mutationFn: ({ id, request }) => mentionReviewApi.setMentionScope(id, request),
    onSuccess: () => {
      // Invalidate both list and detail caches
      queryClient.invalidateQueries({ queryKey: postReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: postReviewKeys.detail(postId) });
    }
  });
}

/**
 * Result from the URL search mutation
 */
export interface PostUrlSearchResult {
  /** The full Reddit post ID (e.g., "t3_abc123") */
  postId: string;
  /** The post data if found */
  data: PostMentionGroupDto;
}

/**
 * usePostUrlSearch - Mutation hook for searching posts by Reddit URL
 *
 * SESSION_S_035_003: Reddit URL search functionality
 *
 * Features:
 * - Parses Reddit URL to extract post ID
 * - Converts short ID to full format (t3_xxx)
 * - Calls API to search for post
 * - Pre-populates query cache on success
 * - Throws descriptive errors for invalid/not-found
 *
 * @returns React Query mutation for URL search
 *
 * @example
 * const { mutateAsync: searchByUrl, isPending, error } = usePostUrlSearch();
 *
 * const handleSearch = async (url: string) => {
 *   try {
 *     const result = await searchByUrl(url);
 *     // Navigate to result.postId
 *     navigate(`/admin/posts/${result.postId}`);
 *   } catch (err) {
 *     // Error displayed to user
 *   }
 * };
 */
export function usePostUrlSearch() {
  const queryClient = useQueryClient();

  return useMutation<PostUrlSearchResult, Error, string>({
    mutationFn: async (url: string): Promise<PostUrlSearchResult> => {
      // Step 1: Parse the URL
      const parseResult = parseRedditUrl(url);

      if (!parseResult.isValid || !parseResult.postId) {
        throw new Error(parseResult.errorMessage || 'Invalid Reddit URL');
      }

      // Step 2: Convert to full post ID format
      const fullPostId = toFullRedditPostId(parseResult.postId);

      // Step 3: Search for the post
      const data = await postReviewApi.searchByPostId(fullPostId);

      if (!data) {
        throw new Error(
          'Post not found. This post may not have been crawled yet, or it may not contain any business mentions.'
        );
      }

      return { postId: fullPostId, data };
    },
    onSuccess: (result) => {
      // Pre-populate the query cache with the found post data
      // This means when navigating to the detail page, data is already available
      queryClient.setQueryData(postReviewKeys.detail(result.postId), result.data);
    }
  });
}

/**
 * useCreateManualMention - Mutation hook for creating a manual mention
 *
 * Sprint 044: Creates a mention for a business the algorithm missed.
 * Supports both searching for an existing business or creating a new one.
 *
 * @param postId - Reddit post ID for cache invalidation
 * @returns React Query mutation result with CreateManualMentionResponse
 */
export function useCreateManualMention(postId: string) {
  const queryClient = useQueryClient();

  return useMutation<CreateManualMentionResponse, Error, CreateManualMentionRequest>({
    mutationFn: (request) => postReviewApi.createManualMention(postId, request),
    onSuccess: () => {
      // Invalidate post review caches (list + detail)
      void queryClient.invalidateQueries({ queryKey: postReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: postReviewKeys.detail(postId) });
      // Invalidate mention review caches (the new mention shows up in mention review too)
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.details() });
    }
  });
}

/**
 * useTriggerMentionNotifications - Mutation hook for triggering notifications
 * on an already-approved mention after contacts are created.
 *
 * Used by MissingContactModal completion in manual mention flow.
 */
export function useTriggerMentionNotifications() {
  return useMutation<
    TriggerNotificationsResponse,
    Error,
    { mentionId: string; businessIds: string[] }
  >({
    mutationFn: ({ mentionId, businessIds }) =>
      mentionReviewApi.triggerMentionNotifications(mentionId, { businessIds })
  });
}
